import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const STATUSES = ["pending", "approved", "paid", "rejected"];

export default function AdminWithdrawals() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/withdrawals${filter ? `?status=${filter}` : ""}`);
    setRows(data.withdrawals);
  };
  useEffect(() => { if (user && user.role === "admin") load(); /* eslint-disable-next-line */ }, [filter, user]);

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/lobby" replace />;

  const update = async (id, status, note) => {
    setBusy(id + status);
    try {
      await api.post(`/admin/withdrawals/${id}/status`, { status, admin_note: note || null });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Admin</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2">Payout audit log</h1>
          <p className="text-sm text-slate-400 mt-2">Cashouts pay out automatically — this log shows every attempt and reference.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["", ...STATUSES].map((s) => (
            <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`admin-filter-${s || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs border ${filter === s ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-white/10 text-slate-400"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="vexis-card overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 border-b border-white/5 text-[11px] font-mono uppercase tracking-widest text-slate-500">
          <div className="col-span-3">Player</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Method</div>
          <div className="col-span-3">Reference</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        <ul className="divide-y divide-white/5">
          {rows.map((w) => (
            <li key={w._id} className="grid grid-cols-12 px-5 py-3 items-center text-sm gap-2" data-testid={`admin-row-${w._id}`}>
              <div className="col-span-3 truncate">
                <div className="text-slate-200 truncate">{w.user_email}</div>
                <div className="text-xs text-slate-500 font-mono">{new Date(w.created_at).toLocaleString()}</div>
              </div>
              <div className="col-span-2">
                <div className="font-mono">${w.amount_usd?.toFixed(2)}</div>
                <div className="text-xs text-slate-500">{w.amount_chips.toLocaleString()} chips</div>
              </div>
              <div className="col-span-2 capitalize text-slate-300">{w.method}</div>
              <div className="col-span-3 text-xs text-slate-400 font-mono truncate" title={w.payout_reference || w.destination}>
                {w.payout_reference || <span className="text-slate-500">{w.destination}</span>}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1 flex-wrap">
                {w.status === "pending" && (
                  <>
                    <button disabled={busy === w._id + "approved"} onClick={() => update(w._id, "approved")} className="text-xs px-2 py-1 rounded-full border border-sky-500/40 text-sky-300" data-testid={`approve-${w._id}`}>Approve</button>
                    <button disabled={busy === w._id + "rejected"} onClick={() => update(w._id, "rejected", "Rejected by admin")} className="text-xs px-2 py-1 rounded-full border border-red-500/40 text-red-300" data-testid={`reject-${w._id}`}>Reject</button>
                  </>
                )}
                {w.status === "approved" && (
                  <button disabled={busy === w._id + "paid"} onClick={() => update(w._id, "paid")} className="text-xs px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-300" data-testid={`pay-${w._id}`}>Mark paid</button>
                )}
                {w.status === "paid" && <span className="pill text-emerald-400 border-emerald-500/40">paid</span>}
                {w.status === "rejected" && <span className="pill text-red-400 border-red-500/40">rejected</span>}
              </div>
            </li>
          ))}
          {rows.length === 0 && <li className="px-5 py-6 text-slate-500 text-sm">No records for this filter.</li>}
        </ul>
      </div>
    </div>
  );
}
